import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import useHomepageVisibility from '../hooks/useHomepageVisibility';

/**
 * Editable speech bubble shown to the left of the Hero mascot.
 *
 * Fetches `/api/hero-bubble` once on mount. When the text is empty the
 * component renders **nothing** so the layout stays clean.
 *
 * Listens for the `hero-bubble:changed` window event so admin edits
 * update without a page reload.
 */
export default function HeroSpeechBubble({ className = '', style = {}, tail = 'right' }) {
  const [text, setText] = useState('');
  const { hero_bubble: visible } = useHomepageVisibility();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/hero-bubble');
        if (alive) setText((r.data?.text || '').trim());
      } catch {
        /* keep current */
      }
    };
    load();
    const onChange = (e) => {
      if (typeof e?.detail?.text === 'string') setText(e.detail.text.trim());
    };
    window.addEventListener('hero-bubble:changed', onChange);
    return () => {
      alive = false;
      window.removeEventListener('hero-bubble:changed', onChange);
    };
  }, []);

  if (!visible) return null;
  if (!text) return null;

  return (
    <div
      data-testid="hero-speech-bubble"
      className={className}
      style={{ position: 'relative', maxWidth: 220, ...style }}
    >
      <div
        style={{
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(244,244,245,0.97) 100%)',
          color: '#18181b',
          borderRadius: 16,
          padding: '10px 14px',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.35,
          boxShadow:
            '0 8px 24px -4px rgba(124,58,237,0.40), 0 2px 6px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.6)',
          border: '1px solid rgba(168,85,247,0.30)',
          textAlign: 'center',
          wordBreak: 'break-word',
          animation: 'jdv-bubble-pop 320ms cubic-bezier(0.22,1.4,0.36,1) both',
        }}
      >
        {text}
        {/* Tail pointing toward the mascot/avatar */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            ...(tail === 'left'
              ? { left: -8, top: '55%' }
              : { right: -8, top: '60%' }),
            transform: 'translateY(-50%) rotate(45deg)',
            width: 14,
            height: 14,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.97), rgba(244,244,245,0.97))',
            ...(tail === 'left'
              ? {
                  borderLeft: '1px solid rgba(168,85,247,0.30)',
                  borderBottom: '1px solid rgba(168,85,247,0.30)',
                }
              : {
                  borderRight: '1px solid rgba(168,85,247,0.30)',
                  borderTop: '1px solid rgba(168,85,247,0.30)',
                }),
            borderRadius: 3,
            boxShadow: tail === 'left'
              ? '-2px 2px 4px -2px rgba(124,58,237,0.20)'
              : '2px -2px 4px -2px rgba(124,58,237,0.20)',
          }}
        />
      </div>
      <style>{`
        @keyframes jdv-bubble-pop {
          0%   { transform: translateX(10px) scale(0.7); opacity: 0; }
          60%  { transform: translateX(-2px) scale(1.04); opacity: 1; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
